import { Link } from "react-router-dom";
import { ArrowRight, Mountain, Layers, Handshake, Phone } from "lucide-react";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { HERO_VIDEO, HERO_POSTER, IMAGES, PRODUCTS, CONTACT } from "../data/siteData";

const Home = () => (
  <main data-testid="home-page">
    {/* HERO */}
    <section className="relative flex min-h-[85vh] items-center justify-center overflow-hidden bg-black" data-testid="hero-section">
      <video
        autoPlay
        loop
        muted
        playsInline
        poster={HERO_POSTER}
        className="absolute inset-0 h-full w-full object-cover"
        data-testid="hero-video"
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/55" />
      <div className="relative z-10 mx-auto max-w-5xl px-4 py-28 text-center text-white">
        <Reveal>
          <div className="mb-10 inline-flex items-center gap-6 md:gap-10 bg-transparent px-12 py-10 md:px-20 md:py-14" data-testid="hero-brand-badge">
            <img
              src="/assets/celestraone-logo-transparent.png"
              alt="CelestraOne Ventures logo"
              className="h-32 md:h-52 w-auto object-contain rounded-2xl bg-white/25 p-4 backdrop-blur-sm [filter:drop-shadow(0_0_20px_rgba(255,255,255,1))_drop-shadow(0_0_60px_rgba(255,255,255,0.7))]"
            />
            <div className="text-left leading-tight">
              <p className="font-display text-4xl md:text-7xl font-bold tracking-tight text-white whitespace-nowrap" data-testid="hero-brand-name">
                CelestraOne Ventures
              </p>
              <p className="mt-2 w-full text-base md:text-2xl font-extrabold uppercase tracking-[0.1em] text-[#D1D5DB]/80 [text-align:justify] [text-align-last:justify] [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_0_24px_rgba(0,0,0,0.8)]">Build to Last and Designed to Impress</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mb-6 text-xs font-bold uppercase tracking-[0.4em] text-white/80" data-testid="hero-eyebrow">
            Granite · Marble · Dolomite · Tomb Stone · Quartz
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter" data-testid="hero-heading">
            From Our Own Quarries to Your Landmark Projects
          </h1>
        </Reveal>
        <Reveal delay={0.3}>
          <p className="mx-auto mt-6 max-w-2xl text-base md:text-lg text-white/85 leading-relaxed" data-testid="hero-subtext">
            CelestraOne Ventures Private Limited mines, processes and delivers granite, marble, dolomite, tomb stone and quartz —
            <br className="hidden md:block" />
            drawn from our own quarries, finished with precision, and trusted by builders, architects and exporters across India and beyond.
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-quote-cta"
              className="inline-flex items-center gap-2 bg-[#D40000] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-1 hover:bg-red-900"
            >
              Request a Quote <ArrowRight size={16} />
            </Link>
            <Link
              to="/products"
              data-testid="hero-products-cta"
              className="inline-flex items-center gap-2 border border-white/60 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-1 hover:bg-white hover:text-black"
            >
              Explore Products
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    {/* WHY US */}
    <section className="py-20 md:py-24 bg-white" data-testid="why-us-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Why CelestraOneVentures</Eyebrow>
          <h2 className="mt-4 max-w-2xl text-4xl md:text-5xl font-semibold tracking-tight" data-testid="why-us-heading">
            Consistency buyers can build on
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {[
            {
              icon: Mountain,
              title: "Own Quarries & Mines",
              text: "We control quality at the source. Our own quarrying operations mean consistent colour, grade and supply across every dispatch.",
            },
            {
              icon: Layers,
              title: "Everything Under One Roof",
              text: "Granite, marble and dolomite — blocks, slabs, tiles and processed mineral — from a single, accountable supplier.",
            },
            {
              icon: Handshake,
              title: "A Trusted Client Base",
              text: "Years of working with builders, architects, dealers and exporters have built relationships that bring clients back.",
            },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.12}>
              <div
                className="group h-full border border-gray-200 bg-[#F8F9FA] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-black"
                data-testid={`why-card-${i}`}
              >
                <f.icon size={32} className="text-[#D40000]" strokeWidth={1.5} />
                <h3 className="mt-6 text-2xl font-medium" data-testid={`why-card-title-${i}`}>{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-600">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* ABOUT PREVIEW */}
    <section className="py-20 md:py-24 bg-[#F8F9FA]" data-testid="about-preview-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Reveal className="order-2 lg:order-1">
          <Eyebrow>About Us</Eyebrow>
          <h2 className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight">
            A mineral business built from the rock up
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-gray-600" data-testid="about-preview-text">
            We operate our own quarries and mines with consistent quality, offering a wide range of granite,
            marble and dolomite under one roof. With years of experience, we serve a trusted client base of
            builders, architects, dealers and exporters — in India and abroad.
          </p>
          <Link
            to="/about"
            data-testid="about-preview-cta"
            className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#D40000] transition-colors hover:text-red-900"
          >
            Read our story <ArrowRight size={16} />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="order-1 lg:order-2">
          <div className="relative">
            <div className="absolute -bottom-4 -right-4 h-full w-full border-2 border-[#D40000]" />
            <img
              src={IMAGES.quarry}
              alt="CelestraOneVentures stone quarry"
              className="relative h-[420px] w-full object-cover"
              data-testid="about-preview-image"
            />
          </div>
        </Reveal>
      </div>
    </section>

    {/* PRODUCTS PREVIEW */}
    <section className="py-20 md:py-24 bg-white" data-testid="products-preview-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Eyebrow>Our Products</Eyebrow>
            <h2 className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight">We Create Your Envision</h2>
          </div>
          <Link
            to="/products"
            data-testid="products-preview-cta"
            className="inline-flex items-center gap-2 border border-black px-6 py-3 text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-1 hover:bg-black hover:text-white"
          >
            View full range <ArrowRight size={16} />
          </Link>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.12}>
              <Link to={`/products?stone=${p.key}`} data-testid={`product-card-${p.key}`} className="group block border border-gray-200 bg-white">
                <div className="h-48 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-medium">{p.name}</h3>
                  <p className="mt-2 text-sm text-gray-600">{p.tagline}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#D40000]">
                    View range <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* INFRASTRUCTURE PREVIEW */}
    <section className="py-20 md:py-24 bg-[#F8F9FA]" data-testid="infra-preview-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-7">
          <img
            src={IMAGES.factory}
            alt="Stone processing machinery"
            className="h-[400px] w-full border border-gray-200 object-cover"
            data-testid="infra-preview-image"
          />
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <Eyebrow>Infrastructure</Eyebrow>
          <h2 className="mt-4 text-4xl md:text-5xl font-semibold tracking-tight">
            Quarries, cutting lines and a yard built for scale
          </h2>
          <p className="mt-6 text-base leading-relaxed text-gray-600">
            From block extraction to precision cutting and polishing, our facility handles the full journey of the
            stone — so what we promise on quality is what arrives at your site or port.
          </p>
          <Link
            to="/infrastructure"
            data-testid="infra-preview-cta"
            className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#D40000] transition-colors hover:text-red-900"
          >
            See our facility <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>

    {/* CTA BAND */}
    <section className="noise-overlay bg-[#0A0A0A] py-20 md:py-24 text-white" data-testid="cta-band-section">
      <div className="mx-auto max-w-4xl px-4 text-center">
        <Reveal>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" data-testid="cta-band-heading">
            Tell us what you're building
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-gray-400">
            Share your requirement — stone type, quantity and destination — and our team will get back with
            availability and pricing.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="cta-band-form-button"
              className="inline-flex items-center gap-2 bg-[#D40000] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-1 hover:bg-red-900"
            >
              Send an Enquiry <ArrowRight size={16} />
            </Link>
            <a
              href={CONTACT.phoneHref}
              data-testid="cta-band-call-button"
              className="inline-flex items-center gap-2 border border-white/50 px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-1 hover:bg-white hover:text-black"
            >
              <Phone size={16} /> {CONTACT.phoneDisplay}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Home;
